/*
 * File: /src/services/contactServices.js
 * Created Date: Friday June 14th 2019
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Saturday, 15th June 2019 9:36:02 am
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */

import { authedAxios } from '../shared/axiosWrapper';
import {
  AD_BASE_CONTACTS_PATH,
  S2_VER_URL,
  AD_BASE_UNSUBSCRIBES_PATH,
  S2_CONTACT_TIMELINE_PATH,
} from '../constants';

export default {
  /**
   * Get Contacts
   * @param {*} after after id for forward pagination
   * @param {*} filters filter array of columns
   * @param {*} sort column sort
   * @param {*} limit query limit
   * @param {*} groups Optional array of group ids
   * @param {*} owners Optional array of owner ids
   * @returns
   */
  getContacts(
    after,
    filters,
    sort,
    limit,
    groups,
    owners,
    email,
    requireEmail,
    validatedEmail
  ) {
    return authedAxios.get(AD_BASE_CONTACTS_PATH, {
      params: {
        filter: filters,
        limit: limit,
        sort: sort,
        after: after,
        groups: groups,
        owners: owners,
        email: email,
        requireEmail: requireEmail,
        // Validated only checks primary email is or is not validated
        validatedEmail: validatedEmail,
      },
    });
  },

  /**
   * Get Contact Source Config
   */
  fetchContactSourceConfig() {
    return authedAxios.get(AD_BASE_CONTACTS_PATH + 'source-map');
  },

  /**
   * Fetch Contact by Id
   * @param {number} contactId
   */
  fetchContactById(contactId) {
    return authedAxios.get(AD_BASE_CONTACTS_PATH + contactId);
  },

  /**
   * Delete Contact by Id
   *
   * @param {*} contactId
   * @returns
   */
  deleteContactById(contactId) {
    return authedAxios.delete(AD_BASE_CONTACTS_PATH + contactId);
  },

  /**
   * Lock Contact
   */
  lockContact(contactId, lock) {
    return authedAxios.put(AD_BASE_CONTACTS_PATH + contactId + '/lock', {
      lock: lock,
    });
  },

  /**
   * Add By Place Id
   *
   * @param {*} placeId
   * @returns
   */
  addByPlaceId(placeId) {
    return authedAxios.post(AD_BASE_CONTACTS_PATH + 'add-place', {
      placeId: placeId,
      // Add type here
    });
  },

  /**
   * Get Contact Sources
   * TODO move this to it's own service
   */
  fetchContactSource() {
    return authedAxios.get(S2_VER_URL + 'contacts-source');
  },

  /**
   * Fetch User Mail Events
   * @param {number} contactId
   */
  fetchUserMailEvents(contactId, eventType = 'mail') {
    return authedAxios.get(
      AD_BASE_CONTACTS_PATH + contactId + '/events/' + eventType
    );
  },

  /**
   * Patch Contact
   *
   * @param {number} contactId
   * @param {ContactInputModel} partialContact
   * @returns
   */
  patchContact(contactId, partialContact) {
    return authedAxios.patch(AD_BASE_CONTACTS_PATH + contactId, partialContact);
  },

  /**
   * New Contact
   *
   * @param {number} contactId
   * @param {ContactInputModel} contact
   * @returns
   */
  newContact(contact) {
    return authedAxios.post(AD_BASE_CONTACTS_PATH, contact);
  },

  /**
   * Send Single Message to Contact
   * TODO Move this to Message Service
   * @param {*} id Contact Id
   * @param {*} message Post body of message, subject, et. al.
   * @returns
   */
  sendSingleMessage(id, message) {
    return authedAxios.post(
      AD_BASE_CONTACTS_PATH + id + '/messages/email',
      message
    );
  },

  /**
   * Fetch Contact Relations
   *
   * @param {*} contactId
   * @returns
   */
  fetchRelations(contactId) {
    return authedAxios.get(AD_BASE_CONTACTS_PATH + contactId + '/relations/');
  },

  /**
   * Delete Contact Relation
   */
  deleteRelation(relationId) {
    return authedAxios.delete(
      AD_BASE_CONTACTS_PATH + 'relations/' + relationId
    );
  },

  /**
   * Fetch Contact Referrals
   *
   * @param {*} contactId
   * @returns
   */
  fetchContactReferrals(contactId) {
    return authedAxios.get(AD_BASE_CONTACTS_PATH + contactId + '/referrals/');
  },

  /**
   * Send Time
   *
   * @param {*} contactId
   * @returns
   */
  fetchContactSendTime(contactId) {
    return authedAxios.get(AD_BASE_CONTACTS_PATH + contactId + '/send-time/');
  },
  mergeContact(contactId1, contactId2, mergedContact) {
    return authedAxios.post(AD_BASE_CONTACTS_PATH + 'merge/', {
      contact_id1: contactId1,
      contact_id2: contactId2,
      merged_contact: mergedContact,
    });
  },

  /**
   * Relate 2 contacts
   * From is the owning side
   * To is the mapping side
   * Type is just a string
   */
  relateContact(contactIdFrom, contactIdTo, relationType) {
    return authedAxios.put(AD_BASE_CONTACTS_PATH + contactIdFrom + '/relate', {
      contact_id_from: contactIdFrom,
      contact_id_to: contactIdTo,
      contact_relation: relationType,
    });
  },

  getUnsubscribes(after, filter, sort, limit, groups, owners, search, email) {
    return authedAxios.get(AD_BASE_UNSUBSCRIBES_PATH, {
      params: {
        filter: filter,
        limit: limit,
        sort: sort,
        after: after,
        groups: groups,
        owners: owners,
        search: search,
        email: search,
      },
    });
  },

  getOpts(after, filter, sort, limit, groups, owners, search, email) {
    return authedAxios.get(AD_BASE_UNSUBSCRIBES_PATH + 'opts', {
      params: {
        filter: filter,
        limit: limit,
        sort: sort,
        after: after,
        groups: groups,
        owners: owners,
        search: search,
        email: search,
      },
    });
  },
  fetchAssets(contactId) {
    return authedAxios.get(AD_BASE_CONTACTS_PATH + contactId + '/assets');
  },
  updateAssets(contactId, payload) {
    return authedAxios.put(
      AD_BASE_CONTACTS_PATH + contactId + '/assets',
      payload
    );
  },
  fetchContactVerificationAssets(contactId) {
    return authedAxios.get(
      AD_BASE_CONTACTS_PATH + contactId + '/verification-assets'
    );
  },
  verifyContact(contactId) {
    return authedAxios.put(AD_BASE_CONTACTS_PATH + contactId + '/verification');
  },
  unVerifyContact(contactId) {
    return authedAxios.delete(
      AD_BASE_CONTACTS_PATH + contactId + '/verification'
    );
  },
  updateVerificationAssets(contactId, payload) {
    return authedAxios.put(
      AD_BASE_CONTACTS_PATH + contactId + '/verification-assets',
      payload
    );
  },

  /**
   * Get Contact Timeline messages
   * This is always in contact descending order
   */
  getTimeline (contactId, limit, beforeId, conversationId/* , unassigned, incomplete */) {
    // ConversationId always take precedence if provided
    // But we need that or contact id
    if (conversationId != undefined) {
      return authedAxios.get(S2_CONTACT_TIMELINE_PATH + 'conversation/' + conversationId, {
        params: {
          // filter: filter,
          limit: limit,
          //sort: sort, // is forced desc by endpoint
          before_id: beforeId,
        }
      });
    }
    if (contactId != undefined) {
      return authedAxios.get(S2_CONTACT_TIMELINE_PATH + 'contact/' + contactId, {
        params: {
          // filter: filter,
          limit: limit,
          //sort: sort, // is forced desc by endpoint
          before_id: beforeId,
        }
      });
    }
    return [];
  },
};
